import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 *
 * Also bridges Teeinblue's legacy `cart:update` event into Horizon's
 * standard cart event contract so the cart badge and drawer refresh without
 * reloading the page.
 */

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      /** @type {HTMLElement & {open?: () => void} | null} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');

      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url || '/cart';
      }
    },
  });
}

/**
 * Opens Horizon's cart drawer from the custom `mc-header` cart link while
 * preserving the link's `/cart` fallback when the drawer isn't rendered.
 *
 * @param {MouseEvent} event
 */
async function handleCustomHeaderCartClick(event) {
  const trigger = event.target instanceof Element ? event.target.closest('.mc-header__cart') : null;
  if (!trigger) return;

  /** @type {HTMLElement & {open?: () => void} | null} */
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (!drawer) return;

  event.preventDefault();
  await customElements.whenDefined('theme-drawer');

  if (drawer.open) {
    drawer.open();
  } else {
    window.location.href = trigger instanceof HTMLAnchorElement ? trigger.href : Theme.routes.cart_url || '/cart';
  }
}

/**
 * Keeps the custom header's cart count synchronized with Horizon cart events.
 *
 * @param {number} itemCount
 */
function renderCustomHeaderCartCount(itemCount) {
  const count = Math.max(0, Number(itemCount) || 0);

  for (const trigger of document.querySelectorAll('.mc-header__cart')) {
    let badge = trigger.querySelector('.mc-header__cart-count');

    if (count === 0) {
      badge?.remove();
      continue;
    }

    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'mc-header__cart-count';
      trigger.append(badge);
    }

    badge.textContent = count < 100 ? String(count) : '99+';
  }
}

/** @param {CartLinesUpdateEvent} event */
function handleCustomHeaderCartUpdate(event) {
  event.promise
    ?.then(({ cart, detail }) => {
      renderCustomHeaderCartCount(cart?.totalQuantity ?? detail?.itemCount ?? 0);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[mc-header-cart] Event promise rejected:', error);
    });
}

/**
 * @param {CustomEvent<{data?: {items?: Array<{id?: number, variant_id?: number, quantity?: number}>, item_count?: number}}>} event
 */
function handleLegacyCartUpdate(event) {
  const ajaxCart = event.detail?.data;
  if (!ajaxCart) return;

  const items = Array.isArray(ajaxCart.items) ? ajaxCart.items : [];
  const latestItem = items.at(-1);
  const configuredForm = window.teeinblue?.formSelector
    ? document.querySelector(window.teeinblue.formSelector)
    : null;
  const productForm = configuredForm?.closest('product-form-component') ?? document.querySelector('product-form-component');
  const formVariantId = productForm?.querySelector('input[name="id"]')?.value;
  const merchandiseId = latestItem?.variant_id ?? latestItem?.id ?? formVariantId;
  const quantity = Number(latestItem?.quantity) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();

  (productForm ?? document).dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: merchandiseId ? [{ merchandiseId: String(merchandiseId), quantity }] : [],
      promise: deferred.promise,
    })
  );

  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
    detail: {
      items,
      itemCount: Number(ajaxCart.item_count) || 0,
      source: 'teeinblue-cart-adapter',
      didError: false,
    },
  });
}

document.addEventListener('click', handleCustomHeaderCartClick);
document.addEventListener(StandardEvents.cartLinesUpdate, handleCustomHeaderCartUpdate);
document.addEventListener('cart:update', handleLegacyCartUpdate);

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}
